// CobrancaPix — PEÇA ÚNICA de cobrança Pix do balcão. Unifica a LÓGICA (o antigo hook usePixCobranca,
// "máquina de cobrança") + o VISUAL (o antigo CartaoQrPix) num componente só. Faz o CICLO COMPLETO de
// UMA cobrança e devolve a bola pra quem chamou:
//   monta → gera o QR (POST na rota do alvo) → o QR aparece na tela do cliente (socket) →
//   acompanha (poll 4s + socket) → confirma SOZINHO (número 62-05 ou valor quebrado único) OU pede o
//   pagador quando só o valor bate (manual) → PAGO.
// Avulso: o ciclo termina aqui (mostra "Pix recebido"). Venda: avisa onPago/onValorAjustado pra a tela
// de finalização continuar (emitir a nota). Quem usa NÃO precisa enxergar o estado por dentro — só
// monta o componente quando decide cobrar e reage aos avisos.
//
// "Cobra ao montar": o gatilho (Gerar QR / Cobrar Pix) vive em quem usa; ao montar, o componente já
// dispara a cobrança e conduz. A lógica de estado/poll/socket é tripa interna desta peça.
//
// Cores 100% por tokens do tema; destaque positivo amarelo (#fde68a + preto), nunca verde/vermelho como
// ÚNICO sinal (daltonismo). Ver catálogo de componentes (Configurações → Catálogo).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, Printer } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api, getToken } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { useToast } from '../Toast';

// Candidato de Pix recebido (mesmo valor) pra o operador confirmar qual é o desta cobrança.
export interface PixCandidato {
  recebimento_id: number;
  valor: string;
  instituicao: string | null;
  origem: string | null;
  data_pagamento: string | null;
}

// pendente (aguarda pagar) → aguardando (caiu um Pix do valor, operador confirma) → pago. cancelado fora.
type Status = 'pendente' | 'aguardando' | 'pago' | 'cancelado';

// ALVO da cobrança: venda (amarrada a um orçamento, o "pago" libera/segue a venda), avulso (amarrada
// a um cliente, o recebimento fica LIVRE pra abater na mão em Contas a Receber) OU título (cobra um
// título de crediário no Contas a Receber: cobra como avulso — gera o recebimento livre —, e quem usa
// casa a baixa do título sozinho com o recebimento_id devolvido no onPago, conciliando de verdade).
export type AlvoCobrancaPix =
  | { tipo: 'venda'; orcamentoId: number; cobreTotal: boolean; cpf?: string }
  | { tipo: 'avulso'; clienteId: number }
  | { tipo: 'titulo'; clienteId: number; contaReceberId?: number };

interface Props {
  alvo: AlvoCobrancaPix;
  /** Valor pedido. O backend pode subir uns centavos (calibração DA-234) → onValorAjustado avisa. */
  valor: number;
  /** 'inline' (Finalizar Venda, QR 64px ao lado) | 'cheio' (Avulso, QR 220px centralizado). */
  layout: 'inline' | 'cheio';
  /** Mostra o botão de comprovante quando pago (gate de quem usa — ex.: caixa = Mercado Pago). */
  mostrarComprovante: boolean;
  /** Disparado na TRANSIÇÃO pra pago (uma vez). No alvo 'titulo'/'avulso' vem o id do recebimento livre
   *  (mp_recebimento_id) gerado — quem usa casa a baixa do título com ele. Venda ignora o argumento. */
  onPago?: (recebimentoId?: number | null) => void;
  /** Backend calibrou o valor (centavo único) — quem usa reflete na linha/total. */
  onValorAjustado?: (novoValor: number) => void;
  /** Cobrança cancelada (pelo operador, por outro ponto, ou falha ao criar) — quem usa volta o estado. */
  onCancelado?: () => void;
}

export function CobrancaPix({ alvo, valor, layout, mostrarComprovante, onPago, onValorAjustado, onCancelado }: Props) {
  const { socket } = useSocket();
  const toast = useToast();
  const cheio = layout === 'cheio';
  const qrPx = cheio ? 220 : 64;

  const [cobranca, setCobranca] = useState<{ cobranca_id: number; qr_data_url: string; valor: string } | null>(null);
  const [status, setStatus] = useState<Status>('pendente');
  const [candidatos, setCandidatos] = useState<PixCandidato[]>([]);
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);

  // Callbacks num ref estável — usados dentro de poll/socket sem re-assinar a cada render.
  const cbRef = useRef({ onPago, onValorAjustado, onCancelado });
  cbRef.current = { onPago, onValorAjustado, onCancelado };

  // Marca paga e dispara onPago só na TRANSIÇÃO (não estava paga → paga). Carrega o recebimento_id
  // (id do Pix livre que confirmou) pra quem cobra por título casar a baixa sozinho.
  const recebidoIdRef = useRef<number | null>(null);
  const pagoDisparadoRef = useRef(false);
  const marcarPaga = useCallback((recebimentoId?: number | null) => {
    if (recebimentoId != null) recebidoIdRef.current = recebimentoId;
    setStatus((s) => (s === 'pago' ? s : 'pago'));
    if (!pagoDisparadoRef.current) {
      pagoDisparadoRef.current = true;
      const recId = recebidoIdRef.current;
      queueMicrotask(() => cbRef.current.onPago?.(recId));
    }
  }, []);

  // --- COBRA AO MONTAR ---  (cobrouRef evita o duplo-disparo do StrictMode em dev)
  const cobrouRef = useRef(false);
  useEffect(() => {
    if (cobrouRef.current) return;
    cobrouRef.current = true;
    void (async () => {
      try {
        const corpo = alvo.tipo === 'venda'
          ? { orcamento_id: alvo.orcamentoId, valor, cpf: alvo.cpf || undefined, cobre_total: alvo.cobreTotal }
          : { cliente_id: alvo.clienteId, valor };
        const rota = alvo.tipo === 'venda' ? '/api/pix/cobrar' : '/api/pix/cobrar-avulso';
        const r = await api.post<{ cobranca_id: number; qr_data_url: string; valor: string }>(rota, corpo);
        setCobranca(r);
        setStatus('pendente');
        // Calibração de centavo único (DA-234): o backend pode ter subido o valor (ex.: 31,00 → 31,03).
        const novo = Number(r.valor);
        if (Number.isFinite(novo) && Math.abs(novo - valor) >= 0.005) cbRef.current.onValorAjustado?.(novo);
        toast.sucesso('QR enviado pra tela do cliente');
      } catch (e: any) {
        // 409 = terminal em uso (já há um QR aberto). Avisa e devolve o controle (quem usa volta o estado).
        toast.erro(e?.message || (e?.status === 409 ? 'Terminal em uso — aguarde a cobrança atual terminar' : 'Falha ao gerar o Pix'));
        cbRef.current.onCancelado?.();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- POLL a cada 4s: confere o status no backend (pago / aguardando+candidatos / cancelado) ---
  useEffect(() => {
    if (!cobranca || status === 'pago' || status === 'cancelado') return;
    const cobrancaId = cobranca.cobranca_id;
    const t = setInterval(() => {
      void (async () => {
        try {
          const r = await api.get<{ status: Status; candidatos?: PixCandidato[]; recebimento_id?: number | null }>(`/api/pix/cobranca/${cobrancaId}`);
          if (r.status === 'pago') { marcarPaga(r.recebimento_id ?? null); return; }
          if (r.status === 'cancelado') { setStatus('cancelado'); cbRef.current.onCancelado?.(); return; }
          if (r.status === 'aguardando' && r.candidatos?.length) {
            setCandidatos(r.candidatos);
            setSelecionado((sel) => sel ?? r.candidatos![0]!.recebimento_id); // pré-seleciona o + recente
            setStatus('aguardando');
          }
        } catch { /* silencioso — rede de segurança */ }
      })();
    }, 4000);
    return () => clearInterval(t);
  }, [cobranca, status, marcarPaga]);

  // --- SOCKET: pago / cancelada (só da NOSSA cobrança) ---
  useEffect(() => {
    if (!socket || !cobranca) return;
    const id = cobranca.cobranca_id;
    const onPagoEv = (d: { cobranca_id: number }) => {
      if (d.cobranca_id !== id) return;
      // O socket não traz o recebimento. No título precisamos dele pra casar a baixa — busca uma vez
      // (o id já está carimbado quando o 'pago' emite); cai pro null (e o poll reconfirma) se falhar.
      if (alvo.tipo === 'titulo') {
        void api.get<{ recebimento_id?: number | null }>(`/api/pix/cobranca/${id}`)
          .then((r) => marcarPaga(r.recebimento_id ?? null))
          .catch(() => marcarPaga(null));
      } else marcarPaga();
    };
    const onCanceladaEv = (d: { cobranca_id: number }) => {
      if (d.cobranca_id !== id) return;
      setStatus('cancelado');
      cbRef.current.onCancelado?.();
    };
    socket.on('pix:pago', onPagoEv);
    socket.on('pix:cobranca_cancelada', onCanceladaEv);
    return () => { socket.off('pix:pago', onPagoEv); socket.off('pix:cobranca_cancelada', onCanceladaEv); };
  }, [socket, cobranca, marcarPaga]);

  // --- AÇÕES ---
  const confirmar = useCallback(async () => {
    if (!cobranca || !selecionado) return;
    setConfirmando(true);
    try {
      await api.post(`/api/pix/cobranca/${cobranca.cobranca_id}/confirmar`, { recebimento_id: selecionado });
      marcarPaga(selecionado); // o operador escolheu qual Pix é — usa esse id pra casar a baixa do título
      toast.sucesso('Pix confirmado');
    } catch (e: any) { toast.erro(e.message); }
    finally { setConfirmando(false); }
  }, [cobranca, selecionado, marcarPaga, toast]);

  // Cancelar QR (válvula de escape): aborta o QR pendente, limpa a tela do cliente e libera o terminal.
  // Libera o estado local mesmo se a rede falhar (o backend reabre sozinho em 10 min).
  const cancelarQR = useCallback(async () => {
    if (cobranca) {
      try { await api.post(`/api/pix/cobranca/${cobranca.cobranca_id}/cancelar`, {}); } catch { /* segue */ }
    }
    setStatus('cancelado');
    cbRef.current.onCancelado?.();
  }, [cobranca]);

  // Comprovante (NÃO fiscal) 55mm da transação Pix já paga. Abre a janela ANTES do await (popup).
  const imprimirComprovante = useCallback(async () => {
    if (!cobranca) return;
    const win = window.open('', '_blank');
    setImprimindo(true);
    try {
      const token = getToken();
      const resp = await fetch(`/api/pix/cobranca/${cobranca.cobranca_id}/comprovante-pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(String(resp.status));
      const blob = await resp.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch (e: any) {
      if (win) win.close();
      toast.erro('Erro ao gerar comprovante Pix: ' + (e?.message ?? e));
    } finally { setImprimindo(false); }
  }, [cobranca, toast]);

  // Cancela no DESMONTE se ainda pendente — sumir o componente (remover a linha / trocar a forma /
  // fechar o modal) nunca pode deixar um QR preso no balcão. Aqui SÓ o POST (sem setState/callback num
  // componente que já saiu de cena — evita o warning de update em desmontado). O botão "Cancelar QR"
  // cuida do caso com o componente vivo (POST + estado + aviso).
  const cobrancaIdRef = useRef<number | null>(null);
  cobrancaIdRef.current = cobranca?.cobranca_id ?? null;
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => () => {
    if ((statusRef.current === 'pendente' || statusRef.current === 'aguardando') && cobrancaIdRef.current) {
      void api.post(`/api/pix/cobranca/${cobrancaIdRef.current}/cancelar`, {}).catch(() => {});
    }
  }, []);

  // =========================== VISUAL (do antigo CartaoQrPix) ===========================

  // Avisinho "também aparece na tela do balcão" — só no QR exposto.
  const avisoBalcao = (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 340, textTransform: 'none' }}>
      O QR também aparece na tela do balcão. Confirma sozinho quando o cliente pagar.
    </div>
  );

  // --- PAGO ---
  if (status === 'pago') {
    const botaoComprovante = mostrarComprovante && cobranca && (
      <button
        type="button" onClick={() => void imprimirComprovante()} disabled={imprimindo}
        title="Imprimir comprovante do Pix (máquina de cartão)"
        style={{
          marginLeft: cheio ? undefined : 'auto', background: '#15803d', color: '#fff', border: 0,
          padding: '6px 12px', borderRadius: 6, fontWeight: 700, fontSize: 13,
          cursor: imprimindo ? 'not-allowed' : 'pointer', opacity: imprimindo ? 0.6 : 1,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        {imprimindo ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
        Comprovante
      </button>
    );
    if (cheio) {
      return (
        <div data-componente="013" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0' }}>
          <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={52} color="#fff" strokeWidth={3} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#166534' }}>PIX RECEBIDO</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 320 }}>
            {alvo.tipo === 'titulo'
              ? <>Título <strong>baixado e conciliado</strong> com este Pix.</>
              : <>Esse valor ficou <strong>livre</strong>. Abata nos títulos do cliente em <strong>Contas a Receber</strong> (baixa parcial → Vincular ao recebimento).</>}
          </div>
          {botaoComprovante}
        </div>
      );
    }
    return (
      <div data-componente="013" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#86efac', borderLeft: '5px solid #15803d', borderRadius: 8, padding: '10px 14px', color: '#000', fontWeight: 700 }}>
        <Check size={20} /> Pix recebido — {formatMoeda(valor)}
        {botaoComprovante}
      </div>
    );
  }

  // --- AGUARDANDO (caiu um Pix de mesmo valor; confirmar o pagador) ---
  if (status === 'aguardando') {
    return (
      <div data-componente="013" style={{ background: '#fef9c3', border: '1px solid #fde047', borderLeft: '5px solid #ca8a04', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#000', marginBottom: 8 }}>
          <Check size={18} /> Pix de {formatMoeda(valor)} recebido — confirme o pagador:
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {candidatos.map((c) => {
            const sel = selecionado === c.recebimento_id;
            const hora = (c.data_pagamento ?? '').replace('T', ' ').slice(11, 16);
            return (
              <button
                key={c.recebimento_id} type="button" onClick={() => setSelecionado(c.recebimento_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer',
                  background: sel ? '#fde68a' : '#fff', border: `2px solid ${sel ? '#ca8a04' : '#e5e7eb'}`,
                  borderRadius: 6, padding: '8px 10px', color: '#000', fontSize: 13,
                }}
              >
                <span style={{ width: 14, height: 14, borderRadius: '50%', border: `4px solid ${sel ? '#ca8a04' : '#cbd5e1'}`, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <b>{c.instituicao || 'Instituição não identificada'}</b>
                  {c.origem && <span style={{ color: '#555' }}> · {c.origem}</span>}
                </span>
                {hora && <span style={{ fontFamily: 'monospace', color: '#555' }}>{hora}</span>}
              </button>
            );
          })}
        </div>
        <button
          type="button" onClick={() => void confirmar()} disabled={!selecionado || confirmando}
          style={{
            marginTop: 10, width: '100%', background: '#15803d', color: '#fff', border: 0,
            padding: '10px 16px', borderRadius: 6, fontWeight: 700, fontSize: 14,
            cursor: !selecionado || confirmando ? 'not-allowed' : 'pointer', opacity: !selecionado || confirmando ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {confirmando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          Confirmar pagamento
        </button>
        <button type="button" onClick={() => void cancelarQR()} className="btn-secundario" style={{ marginTop: 6, width: '100%' }}>
          Cancelar QR
        </button>
      </div>
    );
  }

  // --- QR na tela do cliente (pendente, aguardando pagamento) ---
  if (!cobranca) {
    // Enquanto a 1ª cobrança não voltou do backend (ou já cancelou): nada a mostrar.
    return null;
  }
  if (cheio) {
    return (
      <div data-componente="013" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
        <div style={{ background: '#fff', border: '1px solid #ece2d4', borderRadius: 14, padding: 12 }}>
          <img src={cobranca.qr_data_url} alt="QR Pix" style={{ display: 'block', width: qrPx, height: qrPx }} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 999, padding: '6px 14px' }}>
          Aguardando pagamento…
        </div>
        {avisoBalcao}
      </div>
    );
  }
  return (
    <div data-componente="013" style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--input-bg, #f1f5f9)', border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 8, padding: '10px 14px' }}>
      <img src={cobranca.qr_data_url} alt="QR Pix" style={{ width: qrPx, height: qrPx, borderRadius: 6, background: '#fff' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>QR na tela do cliente · aguardando pagamento…</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, textTransform: 'none' }}>
          Cobrando {formatMoeda(valor)} via Pix · confirma sozinho quando o cliente pagar
        </div>
      </div>
      <button type="button" onClick={() => void cancelarQR()} className="btn-secundario">
        Cancelar QR
      </button>
    </div>
  );
}
